"use client";

import React, { useState, useCallback } from "react";
import { X, Link as LinkIcon, Check } from "lucide-react";
import { usePathname } from "next/navigation";
import type { OptimizerQueryState } from "./query-state";
import { buildShareUrl } from "./query-state";
import { oddsRangeText } from "./odds-range";
import { cn } from "@/lib/utils";
import { getSportsbookBaseColor, getReadableSportsbookColor, hexToRgba } from "./sportsbook-style";
import { comparisonColumnLabel, WHALES_COLUMN } from "@/lib/whales";
import { pricedChoices } from "@/lib/fantasy-props/rows";
import type { PlatformType } from "./utils-v2";
import { isExplicitSlipTypePlatform, getSlipTypeLabel } from "./props-slip-breakevens";
import { gameTimeLabel } from "@/lib/game-date";
import { useTodayYmd } from "@/hooks/use-today-ymd";

interface ChipData {
  label: string;
  category: string;
  onRemove: () => void;
  bookId?: PlatformType;
}

interface ActiveFilterChipsProps {
  state: OptimizerQueryState;
  onChange: (update: Partial<OptimizerQueryState>, options?: { immediate?: boolean }) => void;
  onClearAll: () => void;
  loading?: boolean;
  diamond: boolean;
}

function FilterChip({
  chip,
  loading,
}: {
  chip: ChipData;
  loading?: boolean;
}) {
  const bookColor = chip.bookId ? getSportsbookBaseColor(chip.bookId) : null;
  const bookTextColor = chip.bookId ? getReadableSportsbookColor(chip.bookId) : null;

  return (
    <button
      type="button"
      onClick={chip.onRemove}
      disabled={loading}
      aria-label={`Remove filter: ${chip.label}`}
      className={cn(
        "group/chip inline-flex items-center gap-1 rounded-md border px-2 py-1 sm:py-1 min-h-[36px] sm:min-h-0 text-[11px] font-medium leading-none transition-all duration-150",
        "hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive",
        "active:scale-95",
        "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-white/[0.08] disabled:hover:bg-transparent disabled:hover:text-inherit",
        !bookColor && "border-white/[0.08] bg-white/[0.04] text-zinc-300"
      )}
      style={
        bookColor
          ? {
              borderColor: hexToRgba(bookColor, 0.25),
              backgroundColor: hexToRgba(bookColor, 0.08),
              color: bookTextColor ?? bookColor,
            }
          : undefined
      }
    >
      <span className="truncate max-w-[140px]">{chip.label}</span>
      <X
        className="w-3 h-3 shrink-0 opacity-40 group-hover/chip:opacity-100 group-hover/chip:text-destructive transition-opacity"
        aria-hidden="true"
      />
    </button>
  );
}

export function ActiveFilterChips({
  state,
  onChange,
  onClearAll,
  loading,
  diamond,
}: ActiveFilterChipsProps) {
  const todayYmd = useTodayYmd();
  const priced = pricedChoices(state, diamond);
  const chips: ChipData[] = [];

  state.leagues.forEach((league) => {
    chips.push({
      label: league.toUpperCase(),
      category: "league",
      onRemove: () => {
        const next = state.leagues.filter((l) => l !== league);
        onChange({ leagues: next }, { immediate: true });
      },
    });
  });

  state.statTypes.forEach((stat) => {
    chips.push({
      label: stat,
      category: "stat",
      onRemove: () => {
        const next = state.statTypes.filter((s) => s !== stat);
        onChange({ statTypes: next }, { immediate: true });
      },
    });
  });

  state.matchups.forEach((matchup) => {
    chips.push({
      label: matchup,
      category: "matchup",
      onRemove: () => {
        const next = state.matchups.filter((m) => m !== matchup);
        onChange({ matchups: next }, { immediate: true });
      },
    });
  });

  const timeLabel = gameTimeLabel({ days: state.gameDays, within: state.startsWithin, live: false }, todayYmd);
  if (timeLabel !== "Any time") {
    chips.push({
      label: timeLabel,
      category: "date",
      onRemove: () => {
        onChange({ gameDays: [], startsWithin: 0 }, { immediate: true });
      },
    });
  }

  if (state.minEV > 0) {
    chips.push({
      label: `EV ${state.minEV}%+`,
      category: "ev",
      onRemove: () => {
        onChange({ minEV: 0 }, { immediate: true });
      },
    });
  }

  if (state.minDataPoints > 0) {
    chips.push({
      label: `${state.minDataPoints}+ books`,
      category: "data",
      onRemove: () => {
        onChange({ minDataPoints: 0 }, { immediate: true });
      },
    });
  }

  priced.requiredComparisonBooks.forEach((book) => {
    chips.push({
      label: comparisonColumnLabel(book),
      category: "book",
      ...(book === WHALES_COLUMN ? {} : { bookId: book }),
      onRemove: () => {
        onChange({ requiredComparisonBooks: state.requiredComparisonBooks.filter((other) => other !== book) }, { immediate: true });
      },
    });
  });

  if (state.minOddsToHit > 0 || state.maxOddsToHit < 100) {
    chips.push({
      label: `${state.minOddsToHit}%-${state.maxOddsToHit}%`,
      category: "odds",
      onRemove: () => {
        onChange({ minOddsToHit: 0, maxOddsToHit: 100 }, { immediate: true });
      },
    });
  }

  const oddsRange = oddsRangeText(state);
  if (oddsRange) {
    chips.push({
      label: oddsRange,
      category: "odds",
      onRemove: () => {
        onChange({ minOdds: null, maxOdds: null }, { immediate: true });
      },
    });
  }

  if (state.playerSearch) {
    chips.push({
      label: `"${state.playerSearch}"`,
      category: "player",
      onRemove: () => {
        onChange({ playerSearch: "" }, { immediate: true });
      },
    });
  }

  if (isExplicitSlipTypePlatform(state.platform) && state.slipType) {
    const slipLabel = getSlipTypeLabel(state.platform, state.slipType) ?? state.slipType;
    chips.push({
      label: slipLabel,
      category: "slip",
      onRemove: () => {
        onChange({ slipType: "" }, { immediate: true });
      },
    });
  }

  if (state.boost > 0) {
    chips.push({
      label: `${state.boost}% boost`,
      category: "slip",
      onRemove: () => {
        onChange({ boost: 0 }, { immediate: true });
      },
    });
  }

  if (priced.weights !== null) {
    chips.push({
      label: "Custom weights",
      category: "weights",
      onRemove: () => {
        onChange({ weights: null }, { immediate: true });
      },
    });
  }

  if (state.preset) {
    chips.push({
      label: `Preset: ${state.preset}`,
      category: "preset",
      onRemove: () => {
        onChange({ preset: null }, { immediate: true });
      },
    });
  }

  priced.requiredBooks.forEach((book) => {
    chips.push({
      label: `Requires ${comparisonColumnLabel(book)}`,
      category: "required-book",
      ...(book === WHALES_COLUMN ? {} : { bookId: book as PlatformType }),
      onRemove: () => {
        const next = state.requiredBooks.filter((b) => b !== book);
        onChange({ requiredBooks: next }, { immediate: true });
      },
    });
  });

  if (state.hidePushProps) {
    chips.push({
      label: "No push",
      category: "pushprops",
      onRemove: () => {
        onChange({ hidePushProps: false }, { immediate: true });
      },
    });
  }

  if (state.overUnder) {
    chips.push({
      label: state.overUnder === "over" ? "Overs only" : "Unders only",
      category: "side",
      onRemove: () => {
        onChange({ overUnder: "" }, { immediate: true });
      },
    });
  }

  const pathname = usePathname();
  const [copied, setCopied] = useState(false);

  const handleCopyLink = useCallback(async () => {
    try {
      const url = buildShareUrl(
        state,
        typeof window !== "undefined" ? window.location.origin : "",
        pathname ?? "/optimizer"
      );
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
    }
  }, [state, pathname]);

  if (chips.length === 0) return null;

  const leagueChips = chips.filter((c) => c.category === "league");
  const otherChips = chips.filter((c) => c.category !== "league");

  const clearAll = () => {
    const reset: Partial<OptimizerQueryState> = {};
    if (state.leagues.length > 0) reset.leagues = [];
    if (state.statTypes.length > 0) reset.statTypes = [];
    if (state.matchups.length > 0) reset.matchups = [];
    if (state.gameDays.length > 0) reset.gameDays = [];
    if (state.startsWithin > 0) reset.startsWithin = 0;
    if (state.minEV > 0) reset.minEV = 0;
    if (state.minDataPoints > 0) reset.minDataPoints = 0;
    if (state.requiredComparisonBooks.length > 0) reset.requiredComparisonBooks = [];
    if (state.minOddsToHit > 0) reset.minOddsToHit = 0;
    if (state.maxOddsToHit < 100) reset.maxOddsToHit = 100;
    if (state.minOdds !== null) reset.minOdds = null;
    if (state.maxOdds !== null) reset.maxOdds = null;
    if (state.playerSearch) reset.playerSearch = "";
    if (state.slipType) reset.slipType = "";
    if (state.weights !== null) reset.weights = null;
    if (state.preset !== null) reset.preset = null;
    if (state.requiredBooks.length > 0) reset.requiredBooks = [];
    if (state.hidePushProps) reset.hidePushProps = false;
    if (state.overUnder) reset.overUnder = "";
    onClearAll();
    onChange(reset, { immediate: true });
  };

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1.5 mr-1 pr-2 border-r border-white/[0.08]">
          <span className="text-[10px] data-num font-medium text-zinc-500 tabular-nums">
            {chips.length}
          </span>
          <button
            type="button"
            onClick={clearAll}
            disabled={loading}
            className="text-[10px] font-medium text-zinc-500 hover:text-destructive transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Clear all
          </button>
        </span>

        {leagueChips.length > 0 && (
          <>
            {leagueChips.map((chip) => (
              <FilterChip key={chip.label} chip={chip} loading={loading} />
            ))}
            {otherChips.length > 0 && (
              <span className="w-px h-4 bg-white/[0.08] mx-1 shrink-0" />
            )}
          </>
        )}

        {otherChips.map((chip) => (
          <FilterChip key={`${chip.category}-${chip.bookId ?? chip.label}`} chip={chip} loading={loading} />
        ))}

        <button
          type="button"
          onClick={handleCopyLink}
          className={cn(
            "ml-auto shrink-0 inline-flex items-center gap-1 rounded-md border px-2 py-1 min-h-[44px] md:min-h-0 text-[11px] font-medium leading-none transition-all duration-150",
            "active:scale-95",
            copied
              ? "border-accent-value/30 bg-accent-value/10 text-accent-value"
              : "border-white/[0.08] bg-white/[0.04] text-zinc-400 hover:text-zinc-200 hover:border-white/[0.14]"
          )}
        >
          {copied ? (
            <>
              <Check className="size-3" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <LinkIcon className="size-3" />
              <span>Share</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
